<template>
  <Dialog
    :open="open"
    title="Invite Team Member"
    size="md"
    @update:open="$emit('update:open', $event)"
  >
    <div class="space-y-4">
      <div
        class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900"
      >
        <EnvelopeIcon class="h-6 w-6 text-blue-600 dark:text-blue-400" aria-hidden="true" />
      </div>
      <div class="text-center">
        <p class="text-sm text-gray-500 dark:text-gray-400">
          Send an invitation to join your organization.
        </p>
      </div>

      <form @submit.prevent="handleSubmit" class="space-y-4">
        <div>
          <label for="email" class="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >Email Address</label
          >
          <input
            type="email"
            id="email"
            v-model="form.email"
            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            placeholder="colleague@example.com"
            required
          />
          <p v-if="errors.email" class="mt-1 text-sm text-red-600">{{ errors.email }}</p>
        </div>

        <div>
          <label for="role" class="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >Role</label
          >
          <select
            id="role"
            v-model="form.role"
            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
          <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
            <span class="font-semibold">Member:</span> Can access resources but cannot manage
            settings.<br />
            <span class="font-semibold">Admin:</span> Can manage members, settings, and billing.
          </p>
        </div>
      </form>
    </div>

    <template #footer>
      <div class="sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        <Button type="submit" full-width :loading="isLoading" class="sm:col-start-2" @click="handleSubmit">
          Send Invite
        </Button>
        <Button
          type="button"
          variant="outline"
          full-width
          class="mt-3 sm:col-start-1 sm:mt-0"
          @click="$emit('update:open', false)"
        >
          Cancel
        </Button>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
  import { ref, reactive } from 'vue';
  import { EnvelopeIcon } from '@heroicons/vue/24/outline';
  import { useOrganizationInvites } from '../composables';
  import { Dialog, Button } from '@asabdev/asab-ui';

  defineProps<{
    open: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'update:open', value: boolean): void;
    (e: 'invited'): void;
  }>();

  const { createInvitation } = useOrganizationInvites();
  const isLoading = ref(false);
  const errors = reactive<Record<string, string>>({});

  const form = reactive({
    email: '',
    role: 'member' as 'member' | 'admin',
  });

  const handleSubmit = async () => {
    try {
      isLoading.value = true;
      Object.keys(errors).forEach(key => delete errors[key]);

      // Simple validation
      if (!form.email) {
        errors.email = 'Email is required';
        return;
      }

      await createInvitation({
        email: form.email,
        role: form.role,
      });
      emit('invited');
      emit('update:open', false);
      form.email = '';
      form.role = 'member';
    } catch (err) {
      const error = err as { response?: { data?: { detail?: string } } };
      if (error.response?.data?.detail) {
        errors.email = error.response.data.detail;
      } else {
        errors.email = 'Failed to send invitation';
      }
    } finally {
      isLoading.value = false;
    }
  };
</script>
